/* eslint-disable no-underscore-dangle */
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Center,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@tempmee/react';
import { OutlinePlusIcon } from '@tempmee/react-icons';
import { Spinner } from '../../../../commonComponents/Spinner';
import {
  fetchAuthorizations,
  saveCredentials,
  deleteAuthorization,
} from '../../../../../actions/licensesAction';
import {
  AUTHORIZATION_TABLE_COLUMNS,
  AuthorizationCredentialItem,
} from './AuthorizationCredentialItem';
import { professionShape } from './credentialV2Utils';
import { AuthorizationCredentialAdd } from './AuthorizationCredentialAdd';
import { showErrorMessage } from '../../../../../utils';
import { getMonitoredCredentials } from '../../../../../services/credentials';
import styles from './AuthorizationCredentialSection.module.scss';

export const AuthorizationCredentialSection = ({
  allProfessions,
}) => {
  const dispatch = useDispatch();

  const userId = useSelector(
    (state) => state.users.currentUser?.id,
  );
  const authorizations = useSelector(
    (state) => state.users.authorizations,
  );
  const credentialRules = useSelector(
    (state) => state.users.credentialRules,
  );
  const isLoading = useSelector(
    (state) => state.users.authorizationsLoading,
  );

  const [expandedIds, setExpandedIds] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [monitoredCredentials, setMonitoredCredentials] = useState([]);

  useEffect(() => {
    if (userId) {
      dispatch(fetchAuthorizations(userId));
    }
  }, [dispatch, userId]);

  useEffect(() => {
    if (!userId) return;

    let isMounted = true;

    const fetchMonitoredCreds = async () => {
      try {
        const response = await getMonitoredCredentials(userId);
        if (isMounted) {
          setMonitoredCredentials(response.data?.monitored_credentials || []);
        }
      } catch (error) {
        console.error('Failed to fetch monitored credentials:', error);
      }
    };

    fetchMonitoredCreds();

    return () => {
      isMounted = false;
    };
  }, [userId]);

  const handleToggle = (id) => {
    const isExpanded = expandedIds.includes(id);
    if (isExpanded) {
      setExpandedIds((prev) =>
        prev.filter((item) => item !== id),
      );
    } else {
      setExpandedIds((prev) => [...prev, id]);
    }
  };

  const handleDelete = (authorizationId) => {
    if (!userId || !authorizationId) return;
    if (authorizations?.length <= 1) {
      showErrorMessage({ message: 'Cannot delete the last professional credentials.' });
      return;
    }
    dispatch(deleteAuthorization(userId, authorizationId));
  };

  const handleSave = (updatedAuth) => {
    if (!userId || !updatedAuth?.id) return Promise.resolve();

    // Save changed credentials and/or the CS-confirmed name via their respective APIs
    if (updatedAuth.credentials?.length || updatedAuth._confirmedNameChanged) {
      return dispatch(
        saveCredentials(
          userId,
          updatedAuth,
        ),
      );
    }
    return Promise.resolve();
  };

  const getRulesForAuth = (authorization) => {
    const rule = credentialRules?.find(
      (r) =>
        r.profession === authorization?.profession
        && r.state === authorization?.state,
    );
    return rule?.credentials || [];
  };

  // Loading and empty states span the whole table, like an expanded row.
  const renderMessageRow = (content) => (
    <TableRow>
      <TableCell colSpan={AUTHORIZATION_TABLE_COLUMNS}>
        <Center py={4}>{content}</Center>
      </TableCell>
    </TableRow>
  );

  const renderLoading = () => renderMessageRow(<Spinner />);

  const renderEmpty = () =>
    renderMessageRow(<Typography variant="body2">No authorizations found</Typography>);

  const renderItems = () => {
    if (isLoading) return renderLoading();
    if (!authorizations?.length) return renderEmpty();

    return authorizations.map((authorization) => (
      <AuthorizationCredentialItem
        key={authorization.id}
        authorization={authorization}
        isExpanded={expandedIds.includes(
          authorization.id,
        )}
        handleToggle={handleToggle}
        allProfessions={allProfessions}
        credentialRules={getRulesForAuth(authorization)}
        onSave={handleSave}
        onDelete={handleDelete}
        monitoredCredentials={monitoredCredentials}
      />
    ));
  };

  return (
    <>
      <Typography variant="h6" as="h3">
        Professional Credentials
      </Typography>

      <TableContainer>
        <Table className={styles.table}>
          <TableHead>
            <TableRow className="bg-primary-200">
              <TableCell>Profession</TableCell>
              <TableCell>State</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right" className="w-16">
                <span className="sr-only">Details</span>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{renderItems()}</TableBody>
        </Table>
      </TableContainer>

      {showAddForm ? (
        <AuthorizationCredentialAdd
          onClose={() => setShowAddForm(false)}
        />
      ) : (
        <Center py={10}>
          <Button
            color="primary"
            startIcon={<OutlinePlusIcon size={18} />}
            onClick={() => setShowAddForm(true)}
          >
            License
          </Button>
        </Center>
      )}
    </>
  );
};

AuthorizationCredentialSection.propTypes = {
  allProfessions: PropTypes.arrayOf(PropTypes.shape(professionShape)),
};

AuthorizationCredentialSection.defaultProps = {
  allProfessions: [],
};
